import { Edge, Metadata, ModelAdapter, ModelChanges, Node } from 'ng-diagram'

interface Snapshot {
  nodes: Node[]
  edges: Edge[]
  metadata: Metadata
}

const MAX_HISTORY = 50

const DEFAULT_METADATA: Metadata = {
  viewport: { x: 0, y: 0, scale: 1 },
}

export class HistoryModelAdapter implements ModelAdapter {
  private _nodes: Node[] = []
  private _edges: Edge[] = []
  private _metadata: Metadata = { ...DEFAULT_METADATA }

  private undoStack: Snapshot[] = []
  private redoStack: Snapshot[] = []
  private isTravelingTime = false

  private changeCallbacks: Array<(changes: ModelChanges) => void> = []

  getNodes(): Node[] {
    return this._nodes
  }

  getEdges(): Edge[] {
    return this._edges as Edge[]
  }

  getMetadata(): Metadata {
    return this._metadata
  }

  updateNodes(nodesOrFn: Node[] | ((nodes: Node[]) => Node[])): void {
    this._nodes = typeof nodesOrFn === 'function' ? nodesOrFn(this._nodes) : nodesOrFn
    this.notifyChange()
  }

  updateEdges(edgesOrFn: Edge[] | ((edges: Edge[]) => Edge[])): void {
    this._edges = typeof edgesOrFn === 'function' ? edgesOrFn(this._edges) : edgesOrFn
    this.notifyChange()
  }

  updateMetadata(metadataOrFn: Metadata | ((m: Metadata) => Metadata)): void {
    this._metadata =
      typeof metadataOrFn === 'function' ? metadataOrFn(this._metadata) : metadataOrFn
    this.notifyChange()
  }

  onChange(callback: (changes: ModelChanges) => void): void {
    this.changeCallbacks.push(callback)
  }

  unregisterOnChange(callback: (changes: ModelChanges) => void): void {
    this.changeCallbacks = this.changeCallbacks.filter(cb => cb !== callback)
  }

  undo(): void {
    const snapshot = this.undoStack.pop()
    if (!snapshot) return
    this.redoStack.push(this.snapshot())
    this.applySnapshot(snapshot)
  }

  redo(): void {
    const snapshot = this.redoStack.pop()
    if (!snapshot) return
    this.undoStack.push(this.snapshot())
    this.applySnapshot(snapshot)
  }

  /** Call this before a user action to save the current state to the undo stack. */
  saveSnapshot(): void {
    if (this.isTravelingTime) return
    this.undoStack.push(this.snapshot())
    if (this.undoStack.length > MAX_HISTORY) {
      this.undoStack.shift()
    }
    this.redoStack = []
  }

  toJSON(): string {
    return JSON.stringify({ nodes: this._nodes, edges: this._edges, metadata: this._metadata })
  }

  destroy(): void {
    this.changeCallbacks = []
    this.undoStack = []
    this.redoStack = []
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0
  }

  private snapshot(): Snapshot {
    return {
      nodes: JSON.parse(JSON.stringify(this._nodes)),
      edges: JSON.parse(JSON.stringify(this._edges)),
      metadata: JSON.parse(JSON.stringify(this._metadata)),
    }
  }

  private pushUndo(): void {
    if (this.isTravelingTime) return
    this.undoStack.push(this.snapshot())
    if (this.undoStack.length > MAX_HISTORY) {
      this.undoStack.shift()
    }
    this.redoStack = []
  }

  private applySnapshot(snapshot: Snapshot): void {
    this.isTravelingTime = true
    this._nodes = snapshot.nodes
    this._edges = snapshot.edges
    this._metadata = snapshot.metadata
    this.isTravelingTime = false
    this.notifyChange()
  }

  private notifyChange(): void {
    const changes: ModelChanges = {
      nodes: this._nodes,
      edges: this._edges as Edge[],
      metadata: this._metadata,
    }
    this.changeCallbacks.forEach(cb => cb(changes))
  }
}
